import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Heart, ArrowRight, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DonateSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);

  useEffect(() => {
    const pollPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      const maxAttempts = 5;
      const pollInterval = 2000;

      if (pollAttempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/donations/status/${sessionId}`);
        const data = response.data;

        setPaymentInfo(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setPollAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        setPollAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollAttempts]);

  if (status === 'loading') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#FDFBF7]" data-testid="loading-state">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#FACC15] animate-spin mx-auto mb-4" />
          <p className="text-lg text-[#64748B]">Confirming your donation...</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || status === 'expired') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#FDFBF7] py-24" data-testid="error-state">
        <div className="max-w-md mx-auto text-center px-6">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-3xl font-bold text-[#1E293B] font-['Fredoka'] mb-4">
            {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
          </h1>
          <p className="text-lg text-[#64748B] mb-8">
            {status === 'expired' 
              ? 'Your payment session has expired. Please try again.'
              : 'We couldn\'t verify your payment. Please try again or contact support.'}
          </p>
          <Link
            to="/donate"
            data-testid="try-again-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FACC15] text-[#422006] font-bold px-8 py-4 hover:-translate-y-1 transition-all duration-200"
          >
            Try Again
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    );
  }

  if (status === 'timeout') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#FDFBF7] py-24" data-testid="timeout-state">
        <div className="max-w-md mx-auto text-center px-6">
          <div className="w-20 h-20 bg-[#FACC15] rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="w-10 h-10 text-[#422006]" />
          </div>
          <h1 className="text-3xl font-bold text-[#1E293B] font-['Fredoka'] mb-4">
            Thank You!
          </h1>
          <p className="text-lg text-[#64748B] mb-8">
            Your donation is being processed. You'll receive a confirmation email shortly.
          </p>
          <Link
            to="/"
            data-testid="return-home-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FACC15] text-[#422006] font-bold px-8 py-4 hover:-translate-y-1 transition-all duration-200"
          >
            Return Home
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] bg-[#FDFBF7] py-24" data-testid="success-state">
      <div className="max-w-2xl mx-auto px-6 text-center">
        {/* Success Icon */}
        <div className="w-24 h-24 bg-[#22C55E] rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce">
          <CheckCircle className="w-12 h-12 text-white" />
        </div>

        {/* Thank You Message */}
        <h1 className="text-4xl md:text-5xl font-bold text-[#1E293B] font-['Fredoka'] mb-4">
          Thank You!
        </h1>
        <p className="text-xl text-[#64748B] mb-8">
          Your generous donation will help bring joy, education, and hope to children in Kenya.
        </p>

        {/* Donation Details */}
        {paymentInfo && (
          <div className="bg-white rounded-3xl p-8 shadow-sm mb-8">
            <h2 className="text-xl font-bold text-[#1E293B] font-['Fredoka'] mb-4">
              Donation Details
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-[#E2E8F0]">
                <span className="text-[#64748B]">Amount</span>
                <span className="font-bold text-[#1E293B]">
                  ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#E2E8F0]">
                <span className="text-[#64748B]">Status</span>
                <span className="inline-flex items-center gap-2 text-[#22C55E] font-semibold">
                  <CheckCircle className="w-4 h-4" />
                  Completed
                </span>
              </div>
              {paymentInfo.metadata?.package_name && (
                <div className="flex justify-between items-center py-2">
                  <span className="text-[#64748B]">Package</span>
                  <span className="font-bold text-[#1E293B]">{paymentInfo.metadata.package_name}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Impact Message */}
        <div className="bg-[#FACC15]/10 rounded-2xl p-6 mb-8">
          <Heart className="w-8 h-8 text-[#FF7A59] mx-auto mb-4" />
          <p className="text-lg text-[#1E293B]">
            Every donation helps us create moments of joy and opportunity for children who need it most. 
            You're making a real difference!
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/"
            data-testid="success-home-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FACC15] text-[#422006] font-bold px-8 py-4 hover:-translate-y-1 transition-all duration-200"
          >
            Return Home
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link
            to="/events"
            data-testid="success-events-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#E2E8F0] bg-white text-[#1E293B] font-bold px-8 py-4 hover:bg-[#F3F4F6] transition-all duration-200"
          >
            Learn About Our Events
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DonateSuccessPage;
